let Sentry = null;
const queue = [];
let loading = false;
function isRuntimeError(e) {
  return e instanceof ErrorEvent;
}
let currentReactHandler = (error, info) => {
  queue.push({ type: "react", error, info });
};
function makeReactErrorHandler() {
  return (error, info) => {
    currentReactHandler(error, info);
  };
}
function flush() {
  if (!Sentry) return;
  for (const item of queue.splice(0)) {
    try {
      if (item.type === "react") {
        currentReactHandler(item.error, item.info);
      } else if (item.type === "error") {
        const err = item.event.error ?? new Error(item.event.message);
        Sentry.captureException(err, {
          mechanism: { type: "onerror", handled: false },
          originalException: item.event,
          data: {
            filename: item.event.filename,
            lineno: item.event.lineno,
            colno: item.event.colno
          }
        });
      } else if (item.type === "unhandledrejection") {
        const reason = item.event.reason ?? new Error("unhandledrejection");
        Sentry.captureException(reason, {
          mechanism: { type: "onunhandledrejection", handled: false },
          originalException: item.event
        });
      } else if (item.type === "breadcrumb") {
        Sentry.addBreadcrumb(item.breadcrumb);
      }
    } catch (e) {
      console.warn("Failed to flush queued item", e, item);
    }
  }
}
async function loadSentry(environment) {
  var _a;
  if (!Sentry && !loading) {
    loading = true;
    try {
      const module = await import(
        /* webpackChunkName: "sentry" */
        "@sentry/react"
      );
      module.init({
        dsn: environment.sentryKey,
        environment: environment.sentryEnvironment,
        beforeSend(event) {
          var _a2, _b;
          const ex = (_b = (_a2 = event.exception) == null ? void 0 : _a2.values) == null ? void 0 : _b[0];
          const unhandled = (ex == null ? void 0 : ex.mechanism) && ex.mechanism.handled === false;
          if (unhandled) {
            event.level = "fatal";
            event.tags = { ...event.tags, severity: "critical", origin: "unhandled" };
          } else {
            event.level = event.level ?? "error";
            event.tags = { ...event.tags, severity: "non_critical" };
          }
          return event;
        }
      });
      module.setTag("brand", (_a = window.location) == null ? void 0 : _a.host);
      Sentry = module;
      currentReactHandler = module.reactErrorHandler();
      flush();
    } catch (e) {
      console.error("Failed to load Sentry", e);
    } finally {
      loading = false;
    }
  }
}
function captureEvent(event) {
  if (Sentry) Sentry.captureEvent(event);
  else queue.push({ type: "react", error: event });
}
function captureException(err) {
  if (Sentry) Sentry.captureException(err);
  else queue.push({ type: "react", error: err });
}
function addBreadcrumb(breadcrumb) {
  if (Sentry) Sentry.addBreadcrumb(breadcrumb);
  else queue.push({ type: "breadcrumb", breadcrumb });
}
function attachProvisionalGlobalHandlers(init) {
  let initCalled = false;
  let detached = false;
  const detach = () => {
    if (detached) return;
    detached = true;
    window.removeEventListener("error", onError, true);
    window.removeEventListener("unhandledrejection", onUnhandledRejection, true);
    window.removeEventListener("pointerdown", onPointer, true);
  };
  async function triggerInit() {
    if (initCalled) return;
    initCalled = true;
    try {
      await init();
      detach();
    } catch (e) {
      initCalled = false;
      console.warn("Sentry init failed, will retry on next event", e);
    }
  }
  function onError(e) {
    if (isRuntimeError(e)) {
      queue.push({ type: "error", event: e });
      triggerInit();
    }
  }
  function onUnhandledRejection(e) {
    queue.push({ type: "unhandledrejection", event: e });
    triggerInit();
  }
  function onPointer() {
    triggerInit();
  }
  window.addEventListener("error", onError, true);
  window.addEventListener("unhandledrejection", onUnhandledRejection, true);
  window.addEventListener("pointerdown", onPointer, { once: true, capture: true });
  return detach;
}
export {
  addBreadcrumb,
  attachProvisionalGlobalHandlers,
  captureEvent,
  captureException,
  loadSentry,
  makeReactErrorHandler
};
